import {
	useRefreshToken,
	useTokenExchange,
} from '@/composable/RequestUtils/useAuthorization'
import useAxios from '@/composable/RequestUtils/useAxios'
import useAnalytics from '@/composable/useAnalytics'
import { settings } from '@/settings/DynamicSettings'
import { IUser } from '@/store/interfaces'
import { useCommit, useSelector } from '@/store/rootStore'
import { storageInstances } from '@/utils/storageIntances'

// Lazy getter for auth instance to avoid circular dependencies
let _authInstanceCache: any = null
function getAuthInstance() {
	if (!_authInstanceCache) {
		try {
			// eslint-disable-next-line @typescript-eslint/no-var-requires
			const { useAuthInstance } = require('@zoomcatalog/auth')
			_authInstanceCache = useAuthInstance()
		} catch {
			// Auth instance not available yet
			return null
		}
	}
	return _authInstanceCache
}

export function useAuthStore() {
	const commit = useCommit()
	const isAuthenticated = useSelector((state) => state.auth.isAuthorized)
	const isTeamMember = useSelector((state) => state.auth.isTeamMember)
	const role = useSelector((state) => state.auth.role)
	const company = useSelector((state) => state.auth.company)

	const setAuthorization = (token: string, refreshToken: string) => {
		const { setAuthHeader, setRefreshTokenInterceptor } = useAxios()
		setAuthHeader(token)
		setRefreshTokenInterceptor()
		storageInstances.userToken.set(token)
		storageInstances.refreshToken.set(refreshToken)
		commit({ type: 'auth/setAuthorization' })

		// Sync to auth package state (single source of truth)
		const authInstance = getAuthInstance()
		if (authInstance) {
			authInstance.state.isLoggedIn.value = true
			authInstance.state.accessToken.value = token
			authInstance.state.refreshToken.value = refreshToken
		}
	}

	const setDistributorId = (distributorId: string) => {
		storageInstances.distributorStorage.set(distributorId)

		commit({
			type: 'auth/setDistributorId',
			payload: distributorId,
		})

		commit({
			type: 'auth/setIsPersonalSpace',
			payload: distributorId === settings.PERSONAL_SPACE_ID,
		})
	}

	const setIsTeamMember = (isTeamMember: boolean) => {
		commit({
			type: 'auth/setIsTeamMember',
			payload: isTeamMember,
		})
	}

	const setRole = (role: string) => {
		commit({
			type: 'auth/setRole',
			payload: role,
		})
	}

	const setResetPassword = (token: string) => {
		storageInstances.resetPasswordToken.set(token)
	}

	const removeAuthorization = () => {
		const { removeAuthHeader, removeRefreshTokenInterceptor } = useAxios()
		removeAuthHeader()
		removeRefreshTokenInterceptor()
		storageInstances.userToken.remove()
		storageInstances.refreshToken.remove()
		commit({ type: 'auth/removeAuthorization' })

		// Sync to auth package state (single source of truth)
		const authInstance = getAuthInstance()
		if (authInstance) {
			authInstance.state.isLoggedIn.value = false
			authInstance.state.accessToken.value = null
			authInstance.state.refreshToken.value = null
			authInstance.state.email.value = null
		}
	}

	const removeResetPassword = () => {
		storageInstances.resetPasswordToken.remove()
	}

	const setNewAuthorization = (token: string, refreshToken: string) => {
		const { setAuthHeader } = useAxios()
		setAuthHeader(token)
		storageInstances.userToken.set(token)
		storageInstances.refreshToken.set(refreshToken)
		commit({ type: 'auth/setAuthorization' })

		// Sync to auth package state (single source of truth)
		const authInstance = getAuthInstance()
		if (authInstance) {
			authInstance.state.isLoggedIn.value = true
			authInstance.state.accessToken.value = token
			authInstance.state.refreshToken.value = refreshToken
		}
	}

	const identifyUserAnalytics = async (user: IUser | null) => {
		if (!user) return

		const { identify } = useAnalytics()
		identify(user.id, {
			firstname: user.firstname,
			lastname: user.lastname,
			email: user.email,
			role: role.value,
			company: company.value?.slug,
		})
	}

	const isAuthorized = async (temporalToken?: string) => {
		const previousRefreshToken =
			temporalToken ?? storageInstances.refreshToken.get()

		const isNewAuthWorkflow = useSelector(
			(state) => state.config.isNewAuthWorkflow
		)

		try {
			if (isAuthenticated.value) {
				return true
			} else if (previousRefreshToken) {
				const isTemporalToken = Boolean(temporalToken)

				// if we have the token and refresh token in local storage
				// and not a temporal token, we can say the user is authenticated
				const localUserToken = localStorage.getItem('userToken')
				const localRefreshToken = localStorage.getItem('refreshToken')
				if (
					isNewAuthWorkflow.value &&
					!isTemporalToken &&
					localUserToken &&
					localRefreshToken
				) {
					setAuthorization(localUserToken, localRefreshToken)
					handleDistributorId(isTemporalToken)
					return true
				}

				const {
					userToken,
					refreshToken,
					userTokenOld,
					refreshTokenOld,
					requestToken,
					requestTokenOld,
				} = useRefreshToken()

				if (isNewAuthWorkflow.value) {
					await requestToken(previousRefreshToken)
					handleDistributorId(isTemporalToken)
					setAuthorization(userToken.value, refreshToken.value)
				} else {
					await requestTokenOld({
						refresh_token: previousRefreshToken,
						grant_type: 'refresh_token',
						client_id: import.meta.env.VITE_CRM_CLIENT_ID,
					})
					handleDistributorId(isTemporalToken)
					setAuthorization(userTokenOld.value, refreshTokenOld.value)
				}
			}
			return isAuthenticated.value
		} catch (err) {
			removeAuthorization()
			if (isNewAuthWorkflow.value) {
				return false
			} else {
				window.location.href = import.meta.env.VITE_LOGIN_URL as string
				return false
			}
		}
	}

	const tokenExchange = async (
		sessionId: string,
		supplierId: string,
		distributorId?: string
	) => {
		const { userToken, refreshToken, requestTokenExchange } = useTokenExchange()
		if (distributorId) {
			setDistributorId(distributorId)
		}
		await requestTokenExchange({
			sid: sessionId,
			supplier_id: supplierId,
			...(distributorId ? { distributor_id: distributorId } : {}),
		})
		setAuthorization(userToken.value, refreshToken.value)
	}

	const handleDistributorId = (hasSupplierToken: boolean) => {
		const distributorId = storageInstances.distributorStorage.get()

		if (hasSupplierToken) {
			storageInstances.distributorStorage.remove()
		} else if (distributorId) {
			setDistributorId(distributorId)
		}
	}

	return {
		setAuthorization,
		setNewAuthorization,
		setDistributorId,
		setIsTeamMember,
		setRole,
		setResetPassword,
		removeAuthorization,
		removeResetPassword,
		identifyUserAnalytics,
		tokenExchange,
		isAuthorized,
		company,
		isTeamMember,
		role,
		isAuthenticated,
	}
}
